import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  PurchaseOrderItem,
  PurchaseOrderItemStatus,
} from '@domain/purchasing';
import { Icon, IconSize } from '@shared/icons';
import { APP_CONFIG } from '../../../../config/app.config';
import DashboardStatusChip from '../general/DashboardStatusChip';
import MarkAvailableDialog from './MarkAvailableDialog';
import {
  ALL_STATUSES,
  STATUS_LABEL,
  StatusMilestone,
  formatOverallEta,
  formatStatusDate,
  getConfirmActionLabel,
  getFinalEta,
  getStatusEventLabel,
  isMilestoneOverdue,
  isMilestoneReached,
  statusForMilestone,
} from './utils';

interface PurchaseOrderItemRowProps {
  item: PurchaseOrderItem;
  milestone: StatusMilestone;
  today: Date;
  isUpdating?: boolean;
  onOpen: (item: PurchaseOrderItem) => void;
  onConfirm: (
    item: PurchaseOrderItem,
    milestone: StatusMilestone
  ) => void | Promise<void>;
  onDelayed: (item: PurchaseOrderItem) => void;
  onAddShipment: (item: PurchaseOrderItem) => void;
  onChangeStatus: (
    item: PurchaseOrderItem,
    status: PurchaseOrderItemStatus
  ) => void;
}

const PurchaseOrderItemRow: React.FC<PurchaseOrderItemRowProps> = ({
  item,
  milestone,
  today,
  isUpdating = false,
  onOpen,
  onConfirm,
  onDelayed,
  onAddShipment,
  onChangeStatus,
}) => {
  const colors = APP_CONFIG.ui.theme.colors;
  const statusEnd = milestone.date;
  const finalEta = getFinalEta(item);
  const overdue = isMilestoneOverdue(item, milestone, today);
  const isConfirmed = isMilestoneReached(item, milestone.kind);
  const showActions = Boolean(item.id);
  const needsShipment =
    milestone.kind === 'production_ready' &&
    isConfirmed &&
    !item.shipment &&
    Boolean(item.purchaseOrder);
  const confirmsAvailable =
    statusForMilestone(milestone.kind) === PurchaseOrderItemStatus.AVAILABLE;
  const [editMenuAnchor, setEditMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [statusMenuAnchor, setStatusMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const cellSx = {
    borderBottom: `1px solid ${colors.border}`,
    py: 2,
    px: 2,
    verticalAlign: 'middle' as const,
  };

  const closeEditMenu = () => setEditMenuAnchor(null);
  const closeStatusMenu = () => setStatusMenuAnchor(null);

  const handleConfirmClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (confirmsAvailable) {
      setFinishDialogOpen(true);
      return;
    }
    onConfirm(item, milestone);
  };

  const handleFinishConfirm = async () => {
    await onConfirm(item, milestone);
    setFinishDialogOpen(false);
  };

  return (
    <TableRow
      hover={Boolean(item.purchaseOrder?.id)}
      onClick={() => onOpen(item)}
      sx={{
        cursor: item.purchaseOrder?.id ? 'pointer' : 'default',
        '&:last-child td': { borderBottom: 'none' },
      }}
    >
      <TableCell
        sx={{
          ...cellSx,
          width: '100%',
          maxWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Box
            component="img"
            src={item.product?.image || ''}
            alt=""
            sx={{
              width: 32,
              height: 32,
              minWidth: 32,
              minHeight: 32,
              borderRadius: '4px',
              border: `1px solid ${colors.border}`,
              objectFit: 'contain',
              bgcolor: colors.background,
              flexShrink: 0,
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.visibility = 'hidden';
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <Tooltip title={item.product?.title || item.product?.ean || 'Untitled product'}>
              <Typography
                variant="body2"
                color={colors.primaryText}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.product?.title || item.product?.ean || 'Untitled product'}
              </Typography>
            </Tooltip>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography
                variant="body2"
                color={colors.secondaryText}
                whiteSpace="nowrap"
              >{item.quantity ?? 0} items</Typography>
              {finalEta && (
                <Typography variant="body2" color={colors.secondaryText} whiteSpace="nowrap">
                  {formatOverallEta(finalEta, today)}
                </Typography>
              )}
            </Stack>
          </Box>

        </Stack>
      </TableCell>

      <TableCell
        sx={{
          ...cellSx,
          width: '1%',
          whiteSpace: 'nowrap',
        }}
      >
        <Typography variant="body2" color={colors.primaryText} noWrap>
          {getStatusEventLabel(item, milestone, today)}
        </Typography>

        <Stack direction="row" alignItems="center" gap={0.5}>
          {overdue && (
            <Icon
              icon="AlertTriangle"
              size={IconSize.SMALL}
              color={colors.error}
            />
          )}
          <Typography
            variant="body2"
            fontWeight={500}
            color={overdue ? colors.error : colors.primaryText}
            noWrap
          >
            {formatStatusDate(statusEnd, today)}
          </Typography>
        </Stack>
      </TableCell>

      <TableCell
        align="right"
        sx={{
          ...cellSx,
          width: '1%',
          whiteSpace: 'nowrap',
        }}
      >
        {showActions && isConfirmed ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            {needsShipment ? (
              <Button
                variant="outlined"
                color="info"
                size="small"
                disabled={isUpdating}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddShipment(item);
                }}
              >
               <Typography variant="caption">Add shipment</Typography>
              </Button>
            ) : (
              <DashboardStatusChip label="Confirmed" tone="success" />
            )}
            <IconButton
              size="small"
              disabled={isUpdating}
              onClick={(e) => {
                e.stopPropagation();
                setEditMenuAnchor(e.currentTarget);
              }}
              aria-label="Edit options"
              sx={{
                p: 0.5,
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                color: colors.secondaryText,
              }}
            >
              <Icon
                icon="Edit02"
                size={IconSize.MEDIUM}
                color={colors.secondaryText}
              />
            </IconButton>
            <Menu
              anchorEl={editMenuAnchor}
              open={Boolean(editMenuAnchor)}
              onClose={closeEditMenu}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem
                onClick={() => {
                  closeEditMenu();
                  onDelayed(item);
                }}
              >
                Edit dates
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  const anchor = editMenuAnchor;
                  closeEditMenu();
                  setStatusMenuAnchor(anchor);
                  e.stopPropagation();
                }}
              >
                Edit status
              </MenuItem>
            </Menu>
            <Menu
              anchorEl={statusMenuAnchor}
              open={Boolean(statusMenuAnchor)}
              onClose={closeStatusMenu}
              onClick={(e) => e.stopPropagation()}
            >
              {ALL_STATUSES.map((status) => (
                <MenuItem
                  key={status}
                  selected={item.status === status}
                  onClick={() => {
                    closeStatusMenu();
                    onChangeStatus(item, status);
                  }}
                >
                  {STATUS_LABEL[status]}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        ) : showActions ? (
          <Stack direction="row" alignItems="center" gap={1} justifyContent="flex-end">

            <Stack direction="row" alignItems="center" gap={0} justifyContent="flex-end" border={1} borderColor={colors.border} borderRadius="4px" p={0} >
              <Tooltip title={getConfirmActionLabel(item, milestone)} placement="top">
                <IconButton
                  size="small"
                  sx={{ borderRadius: '4px' }}
                  onClick={handleConfirmClick}
                  color="info"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Icon icon="Check" size={IconSize.MEDIUM} />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title="Delayed" placement="top">
                <IconButton
                  size="small"
                  sx={{ borderRadius: '4px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelayed(item);
                  }}
                >
                  <Icon icon="ClockForward" size={IconSize.MEDIUM} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        ) : null}
      </TableCell>

      <MarkAvailableDialog
        open={finishDialogOpen}
        item={item}
        isUpdating={isUpdating}
        onClose={() => setFinishDialogOpen(false)}
        onConfirm={handleFinishConfirm}
      />
    </TableRow>
  );
};

export default PurchaseOrderItemRow;
